import clsx from 'clsx'
import { useStateMachine } from 'little-state-machine'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CartLineItemDonationInfo } from 'src/components/Cart/CartLineItemDonationInfo'
import { CartLineItemImage } from 'src/components/Cart/CartLineItemImage'
import { CartLineItemInfo } from 'src/components/Cart/CartLineItemInfo'
import { CartLineItemInterval } from 'src/components/Cart/CartLineItemInterval'
import { CartLineItemPrice } from 'src/components/Cart/CartLineItemPrice'
import { Stepper } from 'src/components/Stepper/Stepper'
import { getCalculatedPrices } from 'src/functions/price/getCalculatedPrices'
import { extractSellingPlanIntervalFromName } from 'src/functions/product/extractSellingPlanIntervalFromName'
import { cartItemSellingPlanGenerator } from 'src/functions/product/subscriptionSellingPlanGenerator'
import { useCartLinesUpdateMutation } from 'src/hooks/useCartLinesUpdateMutation'
import { useTimer } from 'src/hooks/useTimer'
import { setIsCartBusy } from 'src/store/storeActions'

import styles from './CartLineItem.module.scss'

import type { CartLine, MoneyV2 } from 'src/data/graphql-generated'

interface CartLineItemProps {
  item: CartLine
  inMinicart?: boolean
  index?: number
  totalItems?: number
}

export const CartLineItem = ({ item, inMinicart = false, index = 0, totalItems = 1 }: CartLineItemProps) => {
  const { t } = useTranslation()
  const { setTimer } = useTimer(500)
  const {
    actions,
    state: { isCartBusy },
  } = useStateMachine({ setIsCartBusy })
  const { mutateAsync: cartLinesUpdate } = useCartLinesUpdateMutation()

  const {
    quantity,
    merchandise: { sellingPlanAllocations },
    sellingPlanAllocation,
  } = item

  const [localQuantity, setLocalQuantity] = useState(quantity)

  // * subscription stuff *
  const { sellingPlans } = cartItemSellingPlanGenerator(sellingPlanAllocation ? sellingPlanAllocations.nodes : [])
  const defaultSellingPlanInterval = extractSellingPlanIntervalFromName(sellingPlanAllocation?.sellingPlan?.name)
  const [isEditingInterval, setIsEditingInterval] = useState(false)
  const [sellingPlanInterval, setSellingPlanInterval] = useState(defaultSellingPlanInterval)

  const weeksString = t('pdp.subscription.weeks')

  const selectElements = Object.values(sellingPlans || {}).map((plan) => ({
    value: plan.interval.toString(),
    label: `${plan.interval} ${weeksString}`,
  }))

  useEffect(() => {
    setLocalQuantity(quantity)
  }, [quantity])

  // sets busy, makes calls
  const loadingHelper = async (asyncFunction: () => Promise<{}>) => {
    actions.setIsCartBusy(true)
    await asyncFunction()
    actions.setIsCartBusy(false)
  }

  const stepperAction = async (newQuantity: number) => {
    if (newQuantity < 0) return
    setLocalQuantity(newQuantity)

    setTimer(async () =>
      loadingHelper(() => cartLinesUpdate({ item, newQuantity, currentSellingPlanInterval: sellingPlanInterval })),
    )
  }

  const setSelectedPlanAndInterval = (interval: string) => {
    setNewSubscriptionInterval(parseInt(interval, 10))
  }

  const setNewSubscriptionInterval = async (newSellingPlanInterval: number) => {
    setSellingPlanInterval(newSellingPlanInterval)
    setIsEditingInterval(false)
    setTimer(async () =>
      loadingHelper(() =>
        cartLinesUpdate({ item, currentSellingPlanInterval: sellingPlanInterval, newSellingPlanInterval }),
      ),
    )
  }

  const allPrices = getCalculatedPrices({
    variant: item.merchandise,
    donationQuantity: quantity,
    cartLineCost: item.cost,
  })
  const includesDonation = Boolean(allPrices.donationPrice)
  const isFirstItem = index === 0
  const isLastItem = totalItems === index + 1

  return (
    <li>
      <div
        className={clsx(
          styles.cartLineItem,
          inMinicart && styles.inMinicart,
          includesDonation && styles.includesDonation,
          isLastItem && styles.lastItem,
          isFirstItem && styles.firstItem,
        )}
      >
        <div className={clsx(styles.imageAndInfoContainer, inMinicart && styles.inMinicart)}>
          <CartLineItemImage item={item} inMinicart={inMinicart} />

          <div className={clsx(styles.itemInfoContainer, inMinicart && styles.inMinicart)}>
            <CartLineItemInfo item={item} inMinicart={inMinicart} />

            <CartLineItemInterval
              selectElements={selectElements}
              sellingPlanInterval={sellingPlanInterval}
              defaultSellingPlanInterval={defaultSellingPlanInterval}
              setSelectedPlanAndInterval={setSelectedPlanAndInterval}
              isEditingInterval={isEditingInterval}
              setIsEditingInterval={setIsEditingInterval}
              inMinicart={inMinicart}
            />
          </div>
        </div>

        <div className={clsx(styles.addRemoveActionsContainer, inMinicart && styles.inMinicart)}>
          <Stepper value={localQuantity} onChange={stepperAction} small={inMinicart} deleteOnZero isBusy={isCartBusy} />
          <CartLineItemPrice itemPriceObjects={allPrices} />
        </div>
      </div>

      <CartLineItemDonationInfo
        donationPrice={allPrices.donationPrice as MoneyV2}
        inMinicart={inMinicart}
        isLastItem={isLastItem}
      />
    </li>
  )
}
